import { useAuth } from '@auth/useAuth'
import AppPaths from '@globals/paths'
import AnonPaths from '@globals/paths/anon'
import {
  register404Listener,
  register503Listener,
  registerIncorrectHtmlContentTypeListener,
  registerRewardEmailRequiredListener,
  unauthenticatedListener,
} from '@kudos/http-client'
import RouteLoading from '@shared/routing/RouteLoading'
import { useEffect, useRef } from 'react'
import { Location, Outlet, useLocation, useNavigate } from 'react-router'
import RootLayout from '../layouts/RootLayout'

export const AwaitAuth = () => {
  const { isAuthenticated, isLoading, initializeFromStorage, clearAuth, isTenantScope } = useAuth()
  const hasInitialized = useRef<boolean>(false)
  const tenantScopeRef = useRef<boolean>(isTenantScope)
  const navigate = useNavigate()
  const location = useLocation()
  // Kept current so the global RewardEmailRequired listener (registered once)
  // always redirects from — and back to — the user's actual location.
  const locationRef = useRef<Location>(location)

  useEffect(() => {
    tenantScopeRef.current = isTenantScope
  }, [isTenantScope])

  useEffect(() => {
    locationRef.current = location
  }, [location])

  useEffect(() => {
    if (!hasInitialized.current) {
      initializeFromStorage()
      // Register listeners before session restore triggers API calls (e.g. /apps via useApps).
      unauthenticatedListener(() => {
        if (tenantScopeRef.current) {
          clearAuth()
        }
      })
      // Register listener to detect 503 (maintenance) and redirect to maintenance page
      register503Listener(() => navigate(AnonPaths.maintenance()))
      // Register listener to detect 404 (not found) and redirect to not found page
      register404Listener(() => navigate(AnonPaths.notFound()))
      // WAF / proxy block pages often return text/html instead of JSON
      registerIncorrectHtmlContentTypeListener(() => {
        if (tenantScopeRef.current) {
          clearAuth()
        }
      })
      // Centralised front door (epic SYN-7280): any reward endpoint returning
      // RewardEmailRequired sends a deskless member to the email-capture screen.
      registerRewardEmailRequiredListener(() => {
        const current = locationRef.current
        const { pathname } = current
        // Never redirectfrom the capture screen itself (which would loop and clobber `from`).
        if (pathname === AppPaths.tenant.rewardsEmailRequired()) {
          return
        }
        navigate(AppPaths.tenant.rewardsEmailRequired(), {
          state: { from: `${current.pathname}${current.search}` },
        })
      })
    }
    hasInitialized.current = true
  }, [clearAuth, initializeFromStorage, navigate])

  if (isLoading) {
    return <RouteLoading />
  }

  return (
    <RootLayout isAuthenticated={isAuthenticated} isTenantScope={isTenantScope}>
      <Outlet />
    </RootLayout>
  )
}
